import { TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { saveDoctor } from '../../../../api/transports';
import { fetchDoctorTypes } from '../../../../redux/slices/transports';
import { useDispatch, useSelector } from '../../../../redux/store';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import PlacesAutocomplete from '../../../../reusable-components/google-maps/PlacesAutocomplete';
import EditModal from '../Settings/EditModal';
import DoctorTypeDropdown from './DoctorTypeDropdown';
import FacilitySelector from './FacilitySelector';

LocationModal.propTypes = {
  edit: PropTypes.bool,
  row: PropTypes.object,
  onAdded: PropTypes.func,
  disabled: PropTypes.bool,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lockFacility: PropTypes.bool,
};

export default function LocationModal({ edit, row, onAdded, disabled = false, facilityId, lockFacility = false }) {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState({
    id: null,
    facilityId: facilityId ?? null,
    doctorName: null,
    doctorTypeId: null,
    buildingName: null,
    address: null,
    addressDisplay: null,
    city: null,
    state: null,
    deleteFlag: false,
  });
  const transportsState = useSelector((state) => state.transports);
  const facilities = transportsState?.data?.facilities ?? [];
  const dataIsLoading = transportsState?.isLoading ?? false;
  const doctorTypes = transportsState?.data?.doctorTypes ?? [];
  const dispatch = useDispatch();

  useEffect(() => {
    if (doctorTypes.length === 0) {
      dispatch(fetchDoctorTypes());
    }
  }, [dispatch, doctorTypes.length]);

  const handleSave = async () => {
    try {
      const response = await saveDoctor(value);
      const result = response.data;
      if (result.success) {
        setOpen(false);
        enqueueSnackbar(result.message || 'Location updated successfully', { variant: 'success' });
        if (onAdded) onAdded(result.data);
      } else {
        enqueueSnackbar(result.message || 'Failed to update Location', { variant: 'error' });
      }
    } catch (error) {
      // Show backend error if present
      const errorMsg = 'Failed to update Location';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    }
  };

  const handleClickOpen = () => {
    setValue({
      id: edit ? row.id : null,
      facilityId: edit ? row.facilityId : facilityId ?? null,
      doctorName: edit ? row.doctorName : null,
      doctorTypeId: edit ? row.doctorTypeId : null,
      buildingName: edit ? row.buildingName : null,
      address: edit ? row.address : null,
      addressDisplay: edit
        ? row.addressDisplay || [row.address, row.city, row.state].filter(Boolean).join(', ')
        : null,
      city: edit ? row.city : null,
      state: edit ? row.state : null,
      deleteFlag: edit ? row.deleteFlag : false,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setValue({
      id: null,
      facilityId: null,
      doctorName: null,
      doctorTypeId: null,
      buildingName: null,
      address: null,
      addressDisplay: null,
      city: null,
      state: null,
      deleteFlag: false,
    });
    setOpen(false);
  };

  // Reusable text field generator
  const renderTextField = (fieldKey, label) => (
    <TextField
      fullWidth
      size="small"
      value={value[fieldKey] || ''}
      onChange={(e) => setValue({ ...value, [fieldKey]: e.target.value })}
      label={label}
      sx={{
        mt: 1,
        '& legend': { display: 'none' },
        '& fieldset': { top: 0 },
        '& .MuiFormLabel-root ': { display: 'none' },
      }}
      autoComplete="off"
    />
  );

  const fields = [
    {
      label: 'Facility',
      element: (
        <FacilitySelector
          facilities={facilities}
          value={value.facilityId}
          onChange={lockFacility ? undefined : (val) => setValue({ ...value, facilityId: val })}
          isLoading={dataIsLoading}
          inputProps={{ 'aria-label': 'Facility Selector', readOnly: !!lockFacility }}
          disabled={!!lockFacility}
          disableClearable={!!lockFacility}
          open={lockFacility ? false : undefined}
        />
      ),
    },
    { label: 'Doctor Name', element: renderTextField('doctorName', 'Doctor Name') },
    {
      label: 'Doctor Type',
      element: (
        <DoctorTypeDropdown
          doctorTypes={doctorTypes}
          value={value.doctorTypeId}
          onChange={(val) => setValue({ ...value, doctorTypeId: val })}
          isLoading={dataIsLoading}
          inputProps={{ 'aria-label': 'Doctor Type Selector' }}
        />
      ),
    },
    { label: 'Building Name', element: renderTextField('buildingName', 'Building Name') },
    {
      label: 'Address',
      element: (
        <PlacesAutocomplete
          value={value.address || ''}
          displayValue={value.addressDisplay ?? value.address ?? ''}
          onChange={(address) => {
            if (!address) {
              setValue((currentValue) => ({
                ...currentValue,
                address: null,
                addressDisplay: null,
                city: null,
                state: null,
              }));
              return;
            }

            setValue((currentValue) => ({
              ...currentValue,
              address: address.address,
              addressDisplay: address.displayValue,
              city: address.city,
              state: address.state,
            }));
          }}
          label="Address"
        />
      ),
    },
  ];

  return (
    <EditModal
      handleClickOpen={handleClickOpen}
      editEntity="Location"
      handleClose={handleClose}
      handleSave={handleSave}
      disableSave={!value.doctorTypeId || !value.facilityId || !value.address}
      disableOpenModal={disabled}
      fields={fields}
      edit={edit}
      open={open}
    />
  );
}
